/** @jsx jsx */
import React, { memo, useMemo, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import Button from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { cssMap, jsx } from '@atlaskit/css';
import type { TriggerProps } from '@atlaskit/popup';
import Spinner from '@atlaskit/spinner';
import { SpotlightManager } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import { PopupTrigger } from '@atlassian/entry-point-popup';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIntl } from '@atlassian/jira-intl';
import type { FiltersMenu_project$key } from '@atlassian/jira-relay/src/__generated__/FiltersMenu_project.graphql';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import messages from './FiltersMenu.messages.tsx';
import { filtersMenuEntryPoint } from './entrypoint.tsx';
import { FiltersMenuOnboarding } from './FiltersMenuOnboarding.tsx';

const styles = cssMap({
	fallback: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '300px',
		height: '200px',
	},
	errorBox: {
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'column',
		height: '120px',
		justifyContent: 'center',
		marginTop: token('space.200'),
		marginRight: token('space.300'),
		marginBottom: token('space.200'),
		marginLeft: token('space.300'),
		gap: token('space.200'),
	},
});

type FiltersMenuProps = {
	defaultJql: string;
	onChangeJql: (jql: string, clearFilter: boolean) => void;
	projectFragment: FiltersMenu_project$key;
};

const experience = 'viewSavedFilters';

const FiltersMenuComponent = ({ defaultJql, onChangeJql, projectFragment }: FiltersMenuProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const projectData = useFragment(
		graphql`
			fragment FiltersMenu_project on JiraProject {
				id @required(action: THROW)
			}
		`,
		projectFragment,
	);

	const { formatMessage } = useIntl();

	const entryPointParams = useMemo(
		() => ({
			projectId: projectData.id,
			first: 50,
			includeProjectClause: true,
		}),
		[projectData.id],
	);

	const entryPointProps = useMemo(
		() => ({
			defaultJql,
			onChangeJql: (jql: string) => onChangeJql(jql, true),
		}),
		[defaultJql, onChangeJql],
	);

	const startExperience = useExperienceStart({
		experience,
		analyticsSource: 'issue-navigator',
	});

	const trigger = useCallback(
		(triggerProps: TriggerProps) => (
			<FiltersMenuOnboarding>
				<Button
					{...triggerProps}
					appearance="subtle"
					iconAfter={() => <ChevronDownIcon label="" size="small" />}
					isSelected={triggerProps['aria-expanded']}
					onClick={() => {
						fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'savedFiltersMenu');
						startExperience();
					}}
					testId="issue-navigator-filters-menu.ui.trigger"
				>
					{formatMessage(messages.menuTitle)}
				</Button>
			</FiltersMenuOnboarding>
		),
		[createAnalyticsEvent, formatMessage, startExperience],
	);

	const fallback = useMemo(
		() => (
			<div css={styles.fallback}>
				<Spinner />
			</div>
		),
		[],
	);

	const errorFallback = useCallback(
		() => (
			<div css={styles.errorBox}>
				<StatusErrorIcon color={token('color.icon')} label="" />
				<Heading size="xsmall">{formatMessage(messages.failedToLoadSavedFilters)}</Heading>
			</div>
		),
		[formatMessage],
	);

	const failExperience = useExperienceFail({
		experience,
	});

	const onError = useCallback(
		({ error }: { error: Error }) => {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'FiltersMenuEntrypoint',
					packageName: 'jiraIssueNavigatorFiltersMenu',
					teamName: 'empanada',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			failExperience('jwmFilters query failed', error);
		},
		[failExperience],
	);

	return (
		<ModalContextProvider>
			<SpotlightManager blanketIsTinted={false}>
				<PopupTrigger
					entryPoint={filtersMenuEntryPoint}
					entryPointParams={entryPointParams}
					entryPointProps={entryPointProps}
					fallback={fallback}
					placement="bottom-end"
					role="dialog"
					shouldRenderToParent
					trigger={trigger}
					label={formatMessage(messages.filtersMenuLabel)}
					errorFallback={errorFallback}
					onError={onError}
				/>
			</SpotlightManager>
		</ModalContextProvider>
	);
};

export const FiltersMenu = memo(FiltersMenuComponent);
